import { getUnsyncedSubmissions, markSubmissionSynced, getAllOfflineSubmissions } from '../utils/offlineStorage';
import { submissionAPI } from '../utils/api';

// Sync configuration
const SYNC_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 5000, // 5 seconds
  BATCH_SIZE: 5, // Process 5 submissions at a time
  SYNC_INTERVAL: 30000, // 30 seconds
  BACKGROUND_SYNC_TAG: 'background-sync'
};

// Track sync status
let syncInProgress = false;
let lastSyncTime = null;
let syncRetries = new Map();

// Enhanced sync with retry logic and batching
export const syncOfflineSubmissions = async (force = false) => {
  // Prevent concurrent syncs unless forced
  if (syncInProgress && !force) {
    return { success: false, message: 'Sync already in progress' };
  }

  try {
    syncInProgress = true;
    const unsyncedSubmissions = await getUnsyncedSubmissions();
    
    if (unsyncedSubmissions.length === 0) {
      return { success: true, message: 'No submissions to sync', count: 0 };
    }


    let successCount = 0;
    let failureCount = 0;
    const results = [];

    // Process submissions in batches
    for (let i = 0; i < unsyncedSubmissions.length; i += SYNC_CONFIG.BATCH_SIZE) {
      const batch = unsyncedSubmissions.slice(i, i + SYNC_CONFIG.BATCH_SIZE);
      
      const batchResults = await Promise.allSettled(
        batch.map(submission => syncSingleSubmission(submission))
      );
      
      batchResults.forEach((result, index) => {
        const submission = batch[index];
        if (result.status === 'fulfilled') {
          successCount++;
          results.push({ id: submission.id, status: 'success', data: result.value });
        } else {
          failureCount++;
          results.push({ id: submission.id, status: 'failed', error: result.reason });
        }
      });
      
      // Small delay between batches to prevent overwhelming the server
      if (i + SYNC_CONFIG.BATCH_SIZE < unsyncedSubmissions.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    lastSyncTime = new Date();
    
    
    // Dispatch sync completion event
    window.dispatchEvent(new CustomEvent('sync-completed', {
      detail: {
        successCount,
        failureCount,
        totalCount: unsyncedSubmissions.length,
        results,
        timestamp: lastSyncTime
      }
    }));

    return {
      success: failureCount === 0,
      message: `Synced ${successCount} of ${unsyncedSubmissions.length} submissions`,
      successCount,
      failureCount,
      results
    };
  } catch (error) {
    return { success: false, message: error.message, error };
  } finally {
    syncInProgress = false;
  }
};

// Sync individual submission with retry logic
const syncSingleSubmission = async (submission) => {
  const submissionId = submission.id;
  const retryCount = syncRetries.get(submissionId) || 0;
  
  try {
    // Prepare submission data for API
    const submissionData = {
      phc: submission.phc,
      name: submission.name,
      address: submission.address,
      responses: submission.responses,
      category: submission.category,
      device_id: submission.device_id
    };

    // Send to API
    const response = await submissionAPI.createSubmission(submissionData);
    
    // Mark as synced in local storage
    await markSubmissionSynced(submissionId);
    
    // Clear retry count on success
    syncRetries.delete(submissionId);
    
    return { success: true, id: submissionId, response };
  } catch (error) {
    
    // Implement retry logic
    if (retryCount < SYNC_CONFIG.MAX_RETRIES) {
      syncRetries.set(submissionId, retryCount + 1);
      
      
      // Exponential backoff
      const delay = SYNC_CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return syncSingleSubmission(submission);
    } else {
      // Max retries reached, give up on this submission
      syncRetries.delete(submissionId);
      throw new Error(`Failed to sync submission after ${SYNC_CONFIG.MAX_RETRIES} retries: ${error.message}`);
    }
  }
};

// Background sync registration
export const registerBackgroundSync = async () => {
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Register periodic background sync
      if ('periodicSync' in registration) {
        try {
          await registration.periodicSync.register('periodic-sync', {
            minInterval: SYNC_CONFIG.SYNC_INTERVAL
          });
        } catch (error) {
        }
      }
      
      return true;
    } catch (error) {
      return false;
    }
  } else {
    return false;
  }
};

// Trigger background sync manually
export const triggerBackgroundSync = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      if (registration.sync) {
        await registration.sync.register(SYNC_CONFIG.BACKGROUND_SYNC_TAG);
        return true;
      } else {
        // Fallback to direct sync
        return await syncOfflineSubmissions();
      }
    } catch (error) {
      return false;
    }
  } else {
    return false;
  }
};

// Get sync status
export const getSyncStatus = () => {
  return {
    inProgress: syncInProgress,
    lastSyncTime,
    retryCount: syncRetries.size,
    supported: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype
  };
};

// Clear retry counts (useful for testing)
export const clearSyncRetries = () => {
  syncRetries.clear();
};

// Enhanced sync listeners with better event handling
export const setupSyncListeners = () => {
  // Sync when coming online
  window.addEventListener('online', async () => {
    
    // Small delay to ensure network is fully available
    setTimeout(async () => {
      const result = await syncOfflineSubmissions();
      
      if (result.success) {
      } else {
      }
    }, 1000);
  });

  // Listen for app online events from offline detection hook
  window.addEventListener('app-online', async (event) => {
    
    setTimeout(async () => {
      const result = await syncOfflineSubmissions();
      
      if (result.success && result.successCount > 0) {
        // Show success notification if supported
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('WeRise Sync Complete', {
            body: `Successfully synced ${result.successCount} submission(s)`,
            icon: '/android-chrome-192x192.png',
            badge: '/android-chrome-192x192.png'
          });
        }
      }
    }, 2000);
  });

  // Listen for sync completion events
  window.addEventListener('sync-completed', (event) => {
  });

  // Initial sync if online
  if (navigator.onLine) {
    setTimeout(() => syncOfflineSubmissions(), 2000);
  }

  // Register background sync if supported
  registerBackgroundSync();
  
  // Set up periodic sync as fallback
  setInterval(async () => {
    if (navigator.onLine && !syncInProgress) {
      const unsynced = await getUnsyncedSubmissions();
      if (unsynced.length > 0) {
        syncOfflineSubmissions();
      }
    }
  }, SYNC_CONFIG.SYNC_INTERVAL);
};

// Keep backward compatibility
export const syncOfflineResponses = async () => {
  return syncOfflineSubmissions();
};